import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { GitBranch, Fingerprint, Eye, Download, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PdfPreview, downloadDoc } from "./PdfPreview";
import { docKindLabel, fmtDateTime, fmtSize, shortHash } from "@/lib/contracts";
import { toast } from "sonner";
import { translateFr as tfr } from "@/i18n";

export const TabVersions = ({ contract }) => {
  const [preview, setPreview] = useState(null);
  const groups = useMemo(() => {
    const map = new Map();
    for (const d of contract.documents || []) {
      const key = `${d.doc_kind}::${d.group || d.filename}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    return [...map.entries()].map(([key, docs]) => ({
      key, docs: docs.sort((a, b) => b.version - a.version),
    })).filter((g) => true);
  }, [contract.documents]);

  return (
    <div className="space-y-4" data-testid="tab-versions">
      <div className="rounded-2xl border border-border bg-surface p-5 flex items-start gap-3">
        <GitBranch className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium">Versions immuables</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {tfr("Chaque envoi crée une nouvelle version horodatée avec son empreinte SHA-256 — aucune version ne peut être modifiée ou supprimée.")}
          </p>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="versions-empty">
          {tfr("Aucune version pour le moment. Ajoutez un document depuis l'onglet Documents.")}
        </div>
      ) : groups.map((g) => (
        <section key={g.key} className="rounded-2xl border border-border bg-surface p-5" data-testid={`version-group-${g.key}`}>
          <div className="flex items-center justify-between mb-4 gap-2">
            <h2 className="text-sm font-medium truncate">{g.docs[0].group || g.docs[0].filename}</h2>
            <span className="text-xs text-muted-foreground rounded-full border border-border px-2 py-0.5 shrink-0">{docKindLabel(g.docs[0].doc_kind)}</span>
          </div>
          <ol className="relative space-y-0">
            {g.docs.map((d, i) => (
              <motion.li key={d.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }}
                className="flex gap-3" data-testid={`version-row-${d.id}`}>
                <div className="flex flex-col items-center">
                  <div className={`h-7 w-7 rounded-full border flex items-center justify-center text-xs font-semibold shrink-0 ${i === 0 ? "bg-red-500/15 border-red-500/40 text-red-400" : "bg-secondary/30 border-border text-muted-foreground"}`}>
                    v{d.version}
                  </div>
                  {i < g.docs.length - 1 && <div className="w-px flex-1 min-h-[14px] bg-secondary/50" />}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 pb-4 min-w-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{d.filename} {i === 0 && <span className="text-xs text-red-400 ml-1">Version actuelle</span>}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>{fmtDateTime(d.uploaded_at)}</span><span className="opacity-40">·</span>
                      <span>{fmtSize(d.size)}</span><span className="opacity-40">·</span>
                      <span className="inline-flex items-center gap-1 font-mono"><Fingerprint className="h-3 w-3" />{shortHash(d.sha256)}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPreview(d)} data-testid={`version-preview-${d.id}`}>
                      <Eye className="h-3 w-3 mr-1" /> Voir
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => downloadDoc(contract.id, d).catch(() => toast.error(tfr("Téléchargement impossible")))} data-testid={`version-download-${d.id}`}>
                      <Download className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </motion.li>
            ))}
          </ol>
        </section>
      ))}

      <div className="rounded-2xl border border-dashed border-border p-5 flex items-center gap-3 text-muted-foreground" data-testid="versions-phase2-note">
        <Sparkles className="h-4 w-4 shrink-0" />
        <p className="text-xs">{tfr("Comparaison côte à côte et résumé IA des différences entre versions — disponibles en Phase 2.")}</p>
      </div>

      <Dialog open={Boolean(preview)} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-[840px]">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{preview?.filename}</DialogTitle>
            <DialogDescription>v{preview?.version} · SHA-256 {shortHash(preview?.sha256)}</DialogDescription>
          </DialogHeader>
          {preview && <PdfPreview cid={contract.id} doc={preview} height={560} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
